import React, { useEffect, useContext, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Box, Divider, Drawer, Typography, useMediaQuery, Button, Badge } from '@mui/material';
import AuthContext from '../../contexts/JWTContext';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import NavSection from '../NavSection';
import Scrollbar from '../Scrollbar';
import CustomLogout from './CustomLogout';
import { DASHBOARD_NAV_HAMBURGER_PX } from './DashboardNavbar';
import useAuth from '../../hooks/useAuth';
import useSettings from '../../hooks/useSettings';
import { useChat } from '../../contexts/ChatContext';
import { capitalize } from 'lodash';
import { NAV_ACCOUNT, buildSidebarSections } from '../../lib/nav.config';
import { getNavIcon } from '../../lib/navIcons';

const decorateItems = (items, waitingResponseCounts) => (
  (items || []).map((item) => {
    const next = {
      ...item,
      icon: getNavIcon(item.icon),
    };
    if (item.children) {
      next.children = decorateItems(item.children, waitingResponseCounts);
    }
    const badgeCount = item.badge ? waitingResponseCounts?.[item.badge] : 0;
    if (badgeCount > 0) {
      next.info = <Badge badgeContent={badgeCount} color="primary" />;
    }
    return next;
  })
);

const DashboardSidebar = (props) => {
  const { settings, saveSettings } = useSettings();

  const handleThemeChange = () => {
    saveSettings({
      ...settings,
      theme: settings.theme === 'LIGHT' ? 'DARK' : 'LIGHT'
    });
  };

  const { onMobileClose, openMobile } = props;
  const location = useLocation();
  const { isInitialized } = useContext(AuthContext);
  const { user } = useAuth();
  const { waitingResponseCounts } = useChat();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down(DASHBOARD_NAV_HAMBURGER_PX));
  const userRoles = user?.roles ?? [];
  const isAsync = user?.is_async;

  const sections = useMemo(() => {
    const fromConfig = buildSidebarSections(userRoles, { isAsync });
    return [
      ...fromConfig.map((section) => ({
        ...section,
        items: decorateItems(section.items, waitingResponseCounts),
      })),
      {
        title: 'Settings',
        items: [
          {
            title: `Switch to ${settings?.theme === 'LIGHT' ? 'Dark' : 'Light'} Mode`,
            icon: settings?.theme === 'LIGHT' ? <DarkModeIcon fontSize="small" /> : <LightModeIcon fontSize="small" />,
            onClick: handleThemeChange
          },
          {
            title: NAV_ACCOUNT.title,
            path: NAV_ACCOUNT.path,
            icon: getNavIcon(NAV_ACCOUNT.icon),
          }
        ]
      },
    ];
  }, [userRoles, isAsync, settings?.theme, waitingResponseCounts]);

  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose();
    }
  }, [location.pathname]);
  if (!isInitialized) {
    return <div>Loading...</div>;
  }

  const content = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%'
      }}
    >
      <Box sx={{ height: 64 }} />
      <Scrollbar options={{ suppressScrollX: true }}>
        <Box sx={{ p: 1 }}>
          <Box
            sx={{
              alignItems: 'center',
              backgroundColor: 'background.default',
              borderRadius: 1,
              px: 2,
              py: 1,
              mb: 2,
              mt: 2,
              display: 'flex',
              justifyContent: 'center'
            }}
          >
            <Typography
              color="textPrimary"
              variant="subtitle1"
              fontWeight="medium"
              sx={{ marginX: 'auto', display: 'flex', alignItems: 'center' }}
            >
              {`${capitalize(user.firstname)} ${capitalize(user.lastname)}`}
            </Typography>
          </Box>
        </Box>
        <Divider />
        <Box sx={{ p: 2 }}>
          <Button
            onClick={handleThemeChange}
            sx={{ mb: 2 }}
            variant="contained"
          >
            Switch Theme (Test)
          </Button>
          {sections.map((section) => (
            <NavSection
              key={section.title}
              pathname={location.pathname}
              sx={{
                '& + &': {
                  mt: 3
                },
                '& .MuiListItem-root': {
                  borderRadius: 1,
                  mb: 0.5,
                  '&:hover': {
                    backgroundColor: 'action.hover'
                  }
                },
                '& .MuiListItemIcon-root': {
                  minWidth: 40
                },
                '& .MuiTypography-subtitle2': {
                  fontSize: '0.875rem',
                  fontWeight: 500
                }
              }}
              {...section}
            />
          ))}
        </Box>
        <Divider />
        <Box sx={{ p: 2, mt: 2 }}>
          <CustomLogout />
        </Box>
      </Scrollbar>
    </Box>
  );

  if (!isMobile) {
    return null;
  }

  return (
    <Drawer
      anchor="left"
      onClose={onMobileClose}
      open={openMobile}
      variant="temporary"
      PaperProps={{
        sx: {
          backgroundColor: 'background.paper',
          width: 280,
          '&::-webkit-scrollbar': {
            display: 'none'
          },
          boxShadow: (theme) => theme.shadows[8]
        }
      }}
    >
      {content}
    </Drawer>
  );
};

DashboardSidebar.propTypes = {
  onMobileClose: PropTypes.func,
  openMobile: PropTypes.bool
};

export default DashboardSidebar;
